import React, {
  useEffect,
  useState,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import Text from '../../../../atomicDesign/atoms/Text';
import Box from '../../../../atomicDesign/atoms/Box';
import SettingsContainer from '../../../../atomicDesign/organisms/SettingsContainer';
import EditContainer from '../../../../atomicDesign/organisms/SettingsContainer/edit';
import IconOnlyButton from '../../../../atomicDesign/molecules/Buttons/IconOnlyButton';
import AdminContext from '../../../../context/admin';
import {
  getNaiveBaselines,
  updateNaiveBaseline,
  deleteNaiveBaseline,
} from '../../../../controller/admin/baseline/naiveBaseline';
import Baseline from './edit';
import type {
  NaiveBaselineRow,
  NaiveBaselineDraft,
  UpdateNaiveBaselineData,
} from './components/types';
import ConfirmDialog from '../../../../atomicDesign/organisms/ConfirmDialog';
import Load from '../../../../atomicDesign/molecules/Load';
import MuiFaIcon from '../../../../atomicDesign/atoms/MuiFaIcon';
import BackButton from '../../../../components/BackButton';
import Toast from '../../../../components/Toast';

const emptyDraft: NaiveBaselineDraft = {
  _id: undefined,
  _vendor: undefined,
  _building: undefined,
  _buildingName: undefined,
  startDate: null,
  endDate: null,
  excludedMonths: [],
};

type AdminContextType = {
  language: {
    global?: {
      save?: string;
      close?: string;
      delete?: string;
    };
    settings?: {
      baseline?: {
        title?: string;
        description?: string;
        searchPlaceholder?: string;
        noBaselines?: string;
        addButtonLabel?: string;
        updated?: string;
        confirmDeleteTitle?: string;
        confirmDeleteMessage?: string;
        activeLabel?: string;
        inactiveLabel?: string;
        createdAt: string;
        backButtonLabel?: string;
      };
    };
    admin: {
      siteInspection?: {
        status?: {
          statusLabel?: string;
        };
      };
    };
    buildings?: {
      details?: {
        form?: {
          buildingName?: string;
        };
      };
    };
    pagination?: {
      rowsPerPage?: string;
      rangeSeparator?: string;
    };
  };
};

export default function BaselineIndex() {
  const { language } = useContext(AdminContext) as AdminContextType;
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<NaiveBaselineRow | null>(null);
  const [baselines, setBaselines] = useState<NaiveBaselineRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [view, setView] = useState<'list' | 'edit'>('list');
  const [activeItem, setActiveItem] = useState<NaiveBaselineDraft>(emptyDraft);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  const fetchNaiveBaselines = useCallback(async () => {
    setLoading(true);
    try {
      const list = await getNaiveBaselines();

      const mapped: NaiveBaselineRow[] = (list ?? []).map((b: any) => ({
        _id: String(b._id),
        _vendor: String(b._vendor ?? ''),
        _building: String(b._building ?? ''),
        _buildingName: b._buildingName ? String(b._buildingName) : undefined,
        createdAt: b.createdAt ? String(b.createdAt) : undefined,
        updatedAt: b.updatedAt ? String(b.updatedAt) : undefined,
        excludedMonths: Array.isArray(b.excludedMonths) ? b.excludedMonths : [],
        active: typeof b.active === 'boolean' ? b.active : undefined,
        locked: typeof b.locked === 'boolean' ? b.locked : undefined,
      }));

      setBaselines(mapped);
    } catch (err) {
      console.error('Failed to fetch naive baselines:', err);
      setBaselines([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === 'list') {
      void fetchNaiveBaselines();
    }
  }, [view, fetchNaiveBaselines]);

  const handleDelete = useCallback(async (row: NaiveBaselineRow) => {
    setLoading(true);
    try {
      await deleteNaiveBaseline(row._id);
      setBaselines((prev) => prev.filter((b) => b._id !== row._id));
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const columns = useMemo(
    () => [
      {
        id: 'buildingName',
        name:
          language?.buildings?.details?.form?.buildingName ?? 'Building Name',
        selector: (row: NaiveBaselineRow) => row._buildingName ?? '-',
        sortable: true,
        cell: (row: NaiveBaselineRow) => (
          <Text variant="body2" noWrap>
            {row._buildingName ?? '-'}
          </Text>
        ),
      },
      {
        id: 'createdAt',
        name: language?.settings?.baseline?.createdAt ?? 'Created',
        selector: (row: NaiveBaselineRow) => row.createdAt ?? '-',
        sortable: true,
        cell: (row: NaiveBaselineRow) => (
          <Text variant="body2" noWrap>
            {row.createdAt
              ? new Date(row.createdAt).toLocaleDateString() +
                ' ' +
                new Date(row.createdAt).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : ''}
          </Text>
        ),
      },
      {
        id: 'status',
        name: language?.admin?.siteInspection?.status?.statusLabel ?? 'Status',
        selector: (row: NaiveBaselineRow) => row.active ?? '-',
        sortable: true,
        cell: (row: NaiveBaselineRow) => (
          <Text variant="body2" noWrap>
            {row.active
              ? (language?.settings?.baseline?.activeLabel ?? 'Active')
              : (language?.settings?.baseline?.inactiveLabel ?? 'Inactive')}
          </Text>
        ),
      },
      {
        name: language?.global?.delete ?? 'Delete',
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        cell: (row: NaiveBaselineRow) => (
          <IconOnlyButton
            color="error"
            onClick={(e: any) => {
              e.stopPropagation();
              setRowToDelete(row);
              setConfirmDeleteDialogOpen(true);
            }}
            icon={<MuiFaIcon name="fa-regular fa-trash" />}
          />
        ),
      },
    ],
    [language]
  );
  const openEdit = (row: NaiveBaselineRow) => {
    setActiveItem({
      ...emptyDraft,
      _id: row._id,
      _vendor: row._vendor,
      _building: row._building,
      _buildingName: row._buildingName,
      excludedMonths: row.excludedMonths ?? [],
      active: row.active,
      locked: row.locked,
    });
    setView('edit');
  };

  const openNew = () => {
    setActiveItem(emptyDraft);
    setView('edit');
  };

  const closeEdit = () => {
    setView('list');
    setActiveItem(emptyDraft);
  };

  const activeBaselines = useMemo(() => {
    return baselines.filter((b) => b.active === true);
  }, [baselines]);

  const handleSave = useCallback(async () => {
    if (!activeItem._id) {
      closeEdit();
      return;
    }

    const updateBaselines = activeBaselines.filter(
      (b) =>
        b._id !== activeItem._id &&
        b.active === true &&
        b._building === activeItem._building
    );

    // If any of the baselines that would be deactivated by this update are locked, block the update to prevent accidentally deactivating a locked baseline.
    if (updateBaselines.some((b) => b.locked === true)) {
      setToast({
        message:
          'An active baseline for this building is locked. Unlock it before saving a new one.',
        type: 'warning',
      });
      return;
    }

    setLoading(true);
    try {
      const patch: UpdateNaiveBaselineData = {};
      if (activeItem._vendor) patch._vendor = activeItem._vendor;
      if (activeItem._building) patch._building = activeItem._building;
      if (activeItem._buildingName)
        patch._buildingName = activeItem._buildingName;
      if (activeItem.excludedMonths)
        patch.excludedMonths = activeItem.excludedMonths;
      // This makes sure that the baseline is not active when created unless the user saves the baseline.
      if (activeItem.active !== undefined) patch.active = true;
      await updateNaiveBaseline(activeItem._id, patch);

      for (const b of updateBaselines) {
        await updateNaiveBaseline(b._id, { active: false });
      }
      await fetchNaiveBaselines();
      closeEdit();
    } finally {
      setLoading(false);
    }
  }, [activeItem, activeBaselines, fetchNaiveBaselines]);

  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        padding: 2,
        borderRadius: 0,
        border: '1px solid #e0e0e0',
        minHeight: '60vh',
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Text variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
          {language?.settings?.baseline?.title ?? 'Baselines'}
        </Text>
        <Text variant="body1" color="text.secondary">
          {language?.settings?.baseline?.description ??
            'Create and configure baselines'}
        </Text>
      </Box>
      {view === 'list' && (
        <>
          <SettingsContainer<NaiveBaselineRow>
            data={activeBaselines}
            columns={columns}
            persistKey="settings-baseline"
            searchPlaceholder={
              language?.settings?.baseline?.searchPlaceholder ??
              'Search in baselines...'
            }
            noDataMessage={
              loading
                ? 'Loading...'
                : (language?.settings?.baseline?.noBaselines ??
                  'No baselines found')
            }
            onRowClick={openEdit}
            onAddClick={openNew}
            addButtonLabel={
              language?.settings?.baseline?.addButtonLabel ?? 'New baseline'
            }
            paginationOptions={{
              rowsPerPageText:
                language?.pagination?.rowsPerPage ?? 'Rows per page',
              rangeSeparatorText: language?.pagination?.rangeSeparator ?? 'of',
            }}
          />
        </>
      )}

      {view === 'edit' && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ mb: 2 }}>
            <BackButton
              onClick={closeEdit}
              label={
                language?.settings?.baseline?.backButtonLabel ??
                'Back to baselines'
              }
            />
          </Box>
          <EditContainer
            close={closeEdit}
            save={handleSave}
            labels={{
              save: language?.global?.save,
              close: language?.global?.close,
            }}
            disabled={activeItem.locked === true && activeItem.active === true}
          >
            <Baseline data={activeItem} setData={setActiveItem} />
          </EditContainer>
        </Box>
      )}

      {loading && <Load />}

      <Toast
        message={toast?.message}
        type={toast?.type}
        close={() => setToast(null)}
      />

      {confirmDeleteDialogOpen && (
        <ConfirmDialog
          title={
            language?.settings?.baseline?.confirmDeleteTitle ?? 'Confirm Delete'
          }
          description={
            language?.settings?.baseline?.confirmDeleteMessage ??
            'Are you sure you want to delete this baseline?'
          }
          cancelLabel={language?.global?.close ?? 'Close'}
          confirmLabel={language?.global?.delete ?? 'Delete'}
          onCancel={() => {
            setConfirmDeleteDialogOpen(false);
            setRowToDelete(null);
          }}
          onConfirm={() => {
            if (!rowToDelete) return;

            void (async () => {
              await handleDelete(rowToDelete);
              setConfirmDeleteDialogOpen(false);
              setRowToDelete(null);
            })();
          }}
        />
      )}
    </Box>
  );
}
